import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { SalvarVendaRequest } from '../../requests/vendas/salvar-venda.request';
import { GetConfiguracoesVendaResponse } from '../../responses/vendas/get-configuracoes-venda.response';
import { GetClienteDocumentoResponse } from '../../responses/vendas/get-cliente-documento.response';
import { GetVendaAnexoResponse } from '../../responses/vendas/get-venda-anexo.response';
import { GetVendaResponse } from '../../responses/vendas/get-venda.response';
import { GetVendaImpressaoResponse } from '../../responses/vendas/get-venda-impressao.response';
import { GetHistoricoProdutoClienteResponse } from '../../responses/vendas/get-historico-produto-cliente.response';
import { GetItensClienteResponse } from '@shared/services/responses/orquestrador-vendas/get-itens-cliente.response';
import { SalvarVendaResponse } from '../../responses/vendas/salvar-venda.response';

/**
 * Documento Comercial — a tela de cadastro do Orcamento e do Pedido de Venda.
 *
 * Separado do ProcessoVendaService de proposito: aquele e o board (cards, colunas, filtros,
 * movimentacao), este e o documento aberto. Sao duas telas, e o board nao precisa carregar os
 * contratos de 81 campos do cabecalho e 98 do item.
 *
 * Aponta para o ORQUESTRADOR nos tres verbos. Ele e quem resolve o escopo de vendedor contra a
 * API.Pessoa, enriquece os itens com o cadastro de produto e confere a credencial do supervisor
 * na liberacao de bloqueio — a API.Vendas nao fala com nenhuma outra API.
 */
@Injectable({
    providedIn: 'root',
})
export class VendaDocumentoService extends CommonService {
    private readonly apiUrl: string = environment.urlApiOrquestradorVendas + 'ProcessoVenda';

    /** A folha, para o que nao precisa de orquestracao nenhuma. */
    private readonly urlApiVendas: string = environment.urlApiVendas;

    constructor(private httpClient: HttpClient) {
        super();
    }

    /**
     * Tudo que a tela precisa para ABRIR: as configuracoes dos dois processos, a geral da empresa
     * e os vendedores que o usuario pode escolher.
     *
     * Carregada UMA vez por abertura, antes do documento — e ela que decide quais secoes
     * aparecem, quais campos sao editaveis e quais textos ja vem preenchidos.
     */
    public getConfiguracoes(): Observable<GetConfiguracoesVendaResponse> {
        return this.httpClient
            .get<GetConfiguracoesVendaResponse>(`${this.apiUrl}/configuracoes`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** O documento inteiro: cabecalho, itens, recebimentos, parcelas, rateio, anexos e liberacoes. */
    public getPorId(idVenda: number): Observable<GetVendaResponse> {
        return this.httpClient
            .get<GetVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * O documento para a folha impressa: o mesmo detalhe, com a URL assinada da imagem de capa de
     * cada item, o cliente completo (contato e endereco) e o nome do vendedor.
     */
    public getImpressao(idVenda: number): Observable<GetVendaImpressaoResponse> {
        return this.httpClient
            .get<GetVendaImpressaoResponse>(`${this.apiUrl}/idVenda/${idVenda}/impressao`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * O cliente escolhido, INTEIRO: cadastro, endereco, contatos, financeiro e as ultimas
     * compras.
     *
     * Uma chamada so porque o card e o modal falam do mesmo cliente no mesmo instante. Separados
     * — como eram — o modal refazia tudo ao abrir, e os dois podiam mostrar numeros diferentes na
     * mesma tela.
     */
    public getCliente(idPessoa: number): Observable<GetClienteDocumentoResponse> {
        return this.httpClient
            .get<GetClienteDocumentoResponse>(`${this.apiUrl}/cliente/idPessoa/${idPessoa}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Link temporario para baixar um anexo ja gravado.
     *
     * Vai DIRETO na API.Vendas, sem passar pelo orquestrador: nao ha nada para juntar aqui — o
     * anexo e a assinatura da URL sao dela, e o orquestrador so repassava a resposta. O binario
     * nao trafega pela API: o storage devolve uma URL assinada e o navegador baixa direto de la.
     */
    public getAnexo(idVendaAnexo: number): Observable<GetVendaAnexoResponse> {
        return this.httpClient
            .get<GetVendaAnexoResponse>(
                `${this.urlApiVendas}Venda/anexo/idVendaAnexo/${idVendaAnexo}`,
                this.addHeaderToken(),
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * As ultimas vendas de UM produto para UM cliente — aba "ultimas vendas" do modal
     * "Detalhes do estoque" (§6.3).
     *
     * Vai direto na FOLHA, como o anexo: a API.Vendas ja confere a permissao e filtra por
     * empresa, e nao ha nada para o orquestrador compor — o kanban que da nome e cor a etapa
     * mora no proprio db_vendas.
     */
    public getHistoricoDoProdutoParaCliente(
        idPessoaCliente: number, idProduto: number, quantidade: number = 10,
    ): Observable<GetHistoricoProdutoClienteResponse> {
        return this.httpClient
            .get<GetHistoricoProdutoClienteResponse>(
                `${this.urlApiVendas}Venda/cliente/idPessoa/${idPessoaCliente}/produto/${idProduto}/historico?quantidade=${quantidade}`,
                this.addHeaderToken(),
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * Os ultimos itens vendidos ao cliente (orcamentos, pedidos e NF-e de saida), com pesquisa
     * opcional pela descricao — aba "Produtos" das ultimas vendas. Vai no ORQUESTRADOR, que junta
     * Vendas e RepoDFe.
     */
    public getItensDoCliente(idPessoaCliente: number, pesquisa: string): Observable<GetItensClienteResponse> {
        return this.httpClient
            .get<GetItensClienteResponse>(
                `${this.apiUrl}/cliente/idPessoa/${idPessoaCliente}/itens?pesquisa=${encodeURIComponent(pesquisa)}`,
                this.addHeaderToken(),
            )
            .pipe(catchError(this.handleError));
    }

    /** Documento novo. O numero e a etapa inicial sao decididos pelo servidor. */
    public incluir(request: SalvarVendaRequest): Observable<SalvarVendaResponse> {
        return this.httpClient
            .post<SalvarVendaResponse>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Alteracao. NAO movimenta o documento: a etapa e movida pelo campo Etapa, que passa
     * pela engrenagem do board (destino habilitado, perfil, motivo e liberacao).
     */
    public alterar(idVenda: number, request: SalvarVendaRequest): Observable<SalvarVendaResponse> {
        return this.httpClient
            .put<SalvarVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }
}
